import { ComponentProps, useEffect, useRef } from 'react';
import Quill from 'quill';
import QuillDelta from 'quill-delta';
import { createGlobalStyle } from 'styled-components';
import classnames from 'classnames';
import { Box, BoxProps, BoxType } from 'reflexbox';
import Delta from 'quill-delta';

interface ITreamsTheme {
	colors: Record<string, string>;
}

const Link = Quill.import('formats/link');

class CustomLink extends Link {
	static sanitize(url: string) {
		if (!(Link.PROTOCOL_WHITELIST as string[]).some((protocol) => url.startsWith(protocol))) {
			return super.sanitize(`http://${url}`);
		}

		return super.sanitize(url);
	}
}
Quill.register(CustomLink, true);

const BoxedStyling = createGlobalStyle<{ theme: ITreamsTheme }>`
	.ql-boxed {
		:not(.ql-readonly) .ql-wrapper {
			position: relative;
		}

		.md-headline, h2, .md-title, h3, .md-subheading-2, h4, .md-subheading-1, h5, .md-body-2, h6, .md-body-1, p {
			color: inherit !important;
		}

		a {
			color: ${(props) => props.theme.colors.primary};
			text-decoration: underline;
		}

		.ql-editor,
		.ql-editor.ql-blank {
			white-space: pre-wrap;
			padding: 0px;
			min-height: 100px;

			p,
			li {
				font-size: 15px;
			}

			outline: none;
		}

		.ql-container, p {
			font-family: inherit !important;
		}

		.ql-container.ql-snow {
			border: none !important;
		}

		.ql-clipboard {
			display: block;
			left: -100000px;
			height: 1px;
			overflow-y: hidden;
			position: absolute;
			top: 50%;
		}
	}

	.ql-wide {
		width: 100%;
	}
`;

const ReadOnlyStyling = createGlobalStyle`
	.ql-readonly {
		cursor: auto;

		ul {
			margin: 0;

			li {
				list-style: inside;
				padding-left: 5px;
			}
		}

        .ql-container, .ql-editor {
            min-height: auto !important;
        }

		.ql-editor {
			overflow: hidden !important;
			overflow-y: hidden !important;
		}

		.ql-editor a {
			cursor: pointer !important;
		}

		.ql-editor * {
			cursor: inherit !important;
		}
	}
`;

export function useQuill({
	theme,
	value,
	onChange,
	readOnly,
	autoFocus,
	placeholder,
}: {
	theme?: string;
	value: QuillDelta;
	onChange?: (value: QuillDelta) => void;
	readOnly?: boolean;
	autoFocus?: boolean;
	placeholder?: string;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const quill = useRef<[Quill] | []>([]);

	const ignoreUntil = useRef<{ date: Date | null }>({ date: null });

	useEffect(() => {
		if (quill.current[0]) {
			return;
		}

		quill.current[0] = new Quill(ref.current!, {
			theme: theme ?? 'snow',
			modules: {
				toolbar: false,
			},
			readOnly: readOnly,
			formats: ['bold', 'italic', 'link', 'list', 'underline'],
			placeholder,
		});

		if (autoFocus) {
			quill.current[0].focus();
		}

		if (onChange) {
			quill.current[0].on('text-change', (_a, _b, source) => {
				if (source === 'api') {
					return;
				}

				ignoreUntil.current.date = new Date(Date.now() + 100);

				onChange?.(quill.current[0]!.getContents() as QuillDelta);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!ignoreUntil.current.date || ignoreUntil.current.date < new Date()) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			quill.current[0]?.setContents(value as any, 'api');
		}
	}, [value]);

	return { ref, quill: quill.current[0] };
}

export function QuillEditor({
	autoFocus,
	value,
	onChange,
	onFocus,
	onBlur,
	inline,
	readOnly,
	wide,
	onQuill,
	placeholder,
	className,
	...rest
}: BoxProps & {
	autoFocus?: boolean;
	value?: QuillDelta;
	onChange?: (value: QuillDelta) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	inline?: boolean;
	readOnly?: boolean;
	wide?: boolean;
	onQuill?: (quill?: Quill) => void;
	placeholder?: string;
	className?: string;
}) {
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const { ref, quill } = useQuill({
		theme: 'snow',
		value: value ?? new Delta(),
		onChange: (value) => onChangeRef.current?.(value),
		readOnly,
		autoFocus,
		placeholder,
	});

	onQuill?.(quill);

	return (
		<div
			className={classnames({
				'ql-boxed': true,
				'ql-readonly': readOnly,
				'ql-inline': inline,
				'ql-wide': wide,
				[className || '']: Boolean(className),
			})}
		>
			<BoxedStyling />
			{readOnly && <ReadOnlyStyling />}
			<div
				className="ql-wrapper"
				onFocus={() => {
					if (quill?.hasFocus()) {
						onFocus?.();
						quill?.focus();
					}
				}}
				onBlur={() => {
					onBlur?.();
					quill?.blur();
				}}
			>
				<Box ref={ref} minHeight={100} {...(rest as ComponentProps<BoxType>)} />
			</div>
		</div>
	);
}
